import { DESKTOP } from '@/src/constants/deviceTypes'
import { DOMAIN, DOMAIN_COUNTRY_MAP } from '@/src/constants/domain'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getBrandName } from '@/src/utils/buildUtils'
import { getAppAgent } from '@/src/utils/deviceType'

/**
 * `x-ct-api-key` for the itinerary orchestrator, resolved from the serving domain and the
 * sales channel.
 *
 * Replaces the upstream's numeric company-id table: those ids only ever encoded a
 * (point of sale × channel) pair, so they are derived here instead of carried around.
 * WEGO and SKYSCANNER rows are omitted — this app does not serve either.
 */

/** Sales channel, matching the upstream's `device` field. */
type ItineraryChannel = 'desktop' | 'pwa' | 'mobile'

/** Point of sale. Taken from `DOMAIN_COUNTRY_MAP`'s Flyin codes (FSA/FEG). */
type ItineraryPos = 'SA' | 'EG' | 'AE'

/**
 * Staging issues a single key for every point of sale and channel, with one exception:
 * Saudi mobile web. Expressed as a default plus that override rather than a full matrix,
 * so a key rotation touches one line instead of five identical ones.
 */
const STAGING_DEFAULT_KEY = 'a729906bc1e886c4afeaee06825aaf87'

const STAGING_OVERRIDES: Partial<Record<ItineraryPos, Partial<Record<ItineraryChannel, string>>>> = {
    SA: { pwa: 'e1994115e48c5faa86cca91f6a05473e' }
}

/** Production issues a distinct key per point of sale and channel. */
const PRODUCTION_KEYS: Record<ItineraryPos, Record<ItineraryChannel, string>> = {
    SA: {
        desktop: 'c37cddca38cea386c02f44673961c2d5',
        pwa: '1f605abbcecd18e9ac7c5a001d3ace3b',
        mobile: 'f5326ce4436acffd620bc101158b0a24'
    },
    EG: {
        desktop: 'a4fdf4948e9fe30a1fa779d7634c7bb5',
        pwa: 'c7170e3587792a76ae84d944b9332fdf',
        mobile: 'aba3d1938f35f6735c88aae758cf73a5'
    },
    AE: {
        desktop: 'b36cbc916af7bfee8207d35ecc625c39',
        pwa: '614faabfb792aeaf74fa27f54a063547',
        mobile: '761c53fa7e4b8e6781610da02b0a38b0'
    }
}

/**
 * Production origins. Everything else — the `me*` staging hosts and localhost — is
 * treated as staging, so a new preview host can never silently pick up a live key.
 */
const PRODUCTION_ORIGINS = new Set([
    'https://www.flyin.com',
    'https://eg.flyin.com',
    'https://ae.flyin.com',
    'https://heya.flyin.com'
])

/** Flyin POS code → the orchestrator's country. */
const POS_BY_DOMAIN_COUNTRY: Record<string, ItineraryPos> = {
    FSA: 'SA',
    FEG: 'EG',
    FAE: 'AE'
}

/**
 * Heya maps to the Saudi point of sale but is issued its own key, one per environment and
 * not split by sales channel, so it is matched on the origin before the POS lookup runs.
 */
const HEYA_KEYS: Record<string, string> = {
    'https://heya.flyin.com': '0f011d502bb2f2c0cabb3d83257c8002',
    'https://me-heya.flyin.com': '5e04feed789d948a005733f6c96180f6'
}

const resolveChannel = (): ItineraryChannel => {
    const deviceType = getDeviceType()
    if (deviceType === DESKTOP) return 'desktop'
    // `getAppAgent` distinguishes the native-app WebView from plain mobile web; the
    // upstream calls the former "mobile" (its MAPP channel) and the latter "pwa".
    const appAgent = getAppAgent(deviceType)
    return appAgent === 'AndroidApp' || appAgent === 'iPhoneApp' ? 'mobile' : 'pwa'
}

/**
 * Empty when no key applies: Cleartrip has none issued, and Flyin domains outside the
 * mapped points of sale (the `FAE` hosts) have none either. Callers omit the header rather
 * than sending a key belonging to somewhere else.
 */
export const getHotelItineraryApiKey = (): string => {
    // Cleartrip is identified by `x-ct-domain` alone and has no keys.
    if (getBrandName() === 'cleartrip') return ''

    const heyaKey = HEYA_KEYS[DOMAIN]
    if (heyaKey) return heyaKey

    const pos = POS_BY_DOMAIN_COUNTRY[DOMAIN_COUNTRY_MAP[DOMAIN] ?? '']
    if (!pos) return ''
    const channel = resolveChannel()
    return PRODUCTION_ORIGINS.has(DOMAIN)
        ? PRODUCTION_KEYS[pos][channel]
        : (STAGING_OVERRIDES[pos]?.[channel] ?? STAGING_DEFAULT_KEY)
}

export default getHotelItineraryApiKey
